import { useState, useEffect } from "react";
import { Download, Smartphone } from "lucide-react";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export function InstallAppButton({ className = "" }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    const ios = /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Already installed → never show.
  if (installed) return null;
  // No native prompt and not installable iOS → hide (don't show a broken button).
  if (!deferred && !isIOS) return null;

  const click = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch (_) {}
      setDeferred(null);
      return;
    }
    if (isIOS) setShowIOSHint((v) => !v);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={click}
        data-testid="install-app-btn"
        aria-label="Install the ManaJodi app"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#781826] bg-[#FAF3E0] border border-[#E2C382] rounded-full px-3 py-1.5 shadow-sm hover:bg-[#F4E6C4] hover:shadow transition-all active:scale-95"
      >
        <Smartphone className="w-3.5 h-3.5 text-[#C59B27]" />
        Install App
        <Download className="w-3.5 h-3.5" />
      </button>
      {showIOSHint && (
        <div className="absolute right-0 mt-2 w-56 text-[11px] leading-relaxed text-[#4A3B3D] bg-white border border-[#EADFCF] rounded-xl shadow-lg p-3 z-20" data-testid="install-ios-hint">
          To install ManaJodi: tap the <span className="font-semibold">Share</span> icon in Safari, then choose <span className="font-semibold">“Add to Home Screen”</span>.
        </div>
      )}
    </div>
  );
}
